
import { useTranslation } from "react-i18next"
import { DashboardStats } from "@/components/dashboard/dashboard-stats"
import { RecentUsers } from "@/components/dashboard/recent-users"
import { RecentPayments } from "@/components/dashboard/recent-payments"
import { NoGymMessage } from "@/components/dashboard/no-gym-message"
import { useUser } from "@/contexts/user-context"
import { useGym } from "@/contexts/gym-context"
import { useView } from "@/contexts/view-context"
import { UserView } from "@/components/user-view/user-view"
import { TrainerView } from "@/components/trainer-view/trainer-view"
import { ReceptionView } from "@/components/reception-view/reception-view"
import { Attendance } from "@/components/dashboard/attendance"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

export default function DashboardPage() {
  const { t } = useTranslation()
  const { user, isLoading: isUserLoading } = useUser()
  const { gyms, selectedGym, selectGym, isLoading: isGymsLoading } = useGym()
  const { viewMode } = useView()

  // Filter gyms based on user's assigned gyms
  const userGyms = user && user.gym ? gyms.filter((gym) => user.gym.includes(gym._id)) : gyms

  // If data is loading, show loading indicator
  if (isUserLoading || isGymsLoading) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>{t("common.loading")}</p>
        </div>
      </div>
    )
  }

  // Determine which view to show based on viewMode
  if (viewMode === "user") {
    return <UserView />
  } else if (viewMode === "trainer") {
    return <TrainerView />
  } else if (viewMode === "reception") {
    return <ReceptionView />
  }

  // Admin view - check if user has gyms
  const hasNoGyms = !gyms || gyms.length === 0

  if (hasNoGyms) {
    return <NoGymMessage />
  }

  // If no gym is selected, show a message
  if (!selectedGym) {
    return (
      <div className="flex-1 flex items-center justify-center p-4 pt-6 md:p-8">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold mb-4">{t("dashboard.selectGymPrompt")}</h2>
          <p className="text-muted-foreground">{t("dashboard.selectGymDescription")}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">{t("dashboard.title")}</h2>
          {(viewMode === "admin" || viewMode === "reception") && userGyms.length > 1 && (
            <Select value={selectedGym} onValueChange={selectGym}>
              <SelectTrigger className="w-[180px] md:w-[220px]">
                <SelectValue placeholder={t("dashboard.selectGym")}>
                  {userGyms.find((gym) => gym._id === selectedGym)?.name || t("dashboard.selectGym")}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {/* {viewMode === "admin" && userGyms.length > 1 && (
                  <SelectItem value="all">{t("dashboard.allGyms")}</SelectItem>
                )} */}
                {userGyms.map((gym) => (
                  <SelectItem key={gym._id} value={gym._id}>
                    {gym.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
      </div>
      <div className="space-y-6">
        <DashboardStats selectedGym={selectedGym} />

        <Attendance selectedGym={selectedGym} />

        <div className="grid gap-6 md:grid-cols-2">
          <RecentUsers selectedGym={selectedGym} className="h-full" />
          <RecentPayments selectedGym={selectedGym} className="h-full" />
        </div>
      </div>
    </div>
  )
}
